import { effect, inject, Injectable, signal } from "@angular/core";

import { from, Observable, of, Subject } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { io, Socket } from "socket.io-client";

import { AuthService } from "@core/services/auth.service";
import { LoggerService } from "@core/services/logger.service";
import { environment } from "@env/environment";
import { FileUploadService } from "@shared/api/file-upload/file-upload.service";
import { BaseHttpService } from "@shared/services/base-http.service";
import { ToastService } from "@shared/services/toast.service";

import { AiAssistStreamEnd, AiChatEvent, AiChatStreamChunk, AiChatStreamEnd, AiSuggestion } from "../models/document-ai.model";
import { AttachmentContext, DocumentContext } from "../types/attachment.types";

export type AiAssistEvent =
  | { status: "started"; actionId: string }
  | { status: "thought"; thought: string; actionId: string }
  | {
      status: "completed";
      finalOutput: AiAssistStreamEnd["finalOutput"];
      actionId: string;
      suggestions?: AiSuggestion[];
      information?: AiAssistStreamEnd["information"];
    };

@Injectable({
  providedIn: "root"
})
export class DocumentAiHttpService extends BaseHttpService {
  private readonly _auth = inject(AuthService);
  private readonly _logger = inject(LoggerService);
  private readonly _toast = inject(ToastService);
  private readonly _fileUploadService = inject(FileUploadService);
  override baseEndpoint = "document-ai";
  private _socket: Socket | undefined;
  readonly isConnected = signal(false);
  // Active in-flight streams keyed by requestId (supports multiple concurrent documents/requests)
  private readonly _assistRequests = new Map<string, Subject<AiAssistEvent>>();
  private readonly _chatRequests = new Map<string, Subject<AiChatEvent>>();
  private _activeStreamCount = 0;

  // Ensure we register stream handlers once per socket instance
  private _handlersRegisteredForSocketKey: string | null = null;
  private _onlineListener: (() => void) | undefined;
  private _heartbeatInterval: NodeJS.Timeout | null = null;
  private _currentUserId: string | null = null;

  private get socket(): Socket {
    const currentUser = this._auth.currentUser();
    if (!currentUser?.id) {
      throw new Error("Cannot create socket: User not authenticated");
    }

    // Create socket per user with namespace /userId
    const namespace = `/${currentUser.id}`;
    const socketKey = `socket_${currentUser.id}`;
    
    if (!this._socket || (this._socket as any)._namespaceKey !== socketKey) {
      // Disconnect old socket if user changed
      if (this._socket) {
        this._socket.disconnect();
      }

      this._socket = io(`${environment.wsUrl}${namespace}`, {
        auth: (cb) => {
          // Dynamic auth callback - gets fresh token on each connection attempt
          cb({ token: this._auth.accessToken });
        },
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 500,
        reconnectionDelayMax: 2000,
        autoConnect: true,
        timeout: 20000
      });
      
      // Store namespace key for tracking
      (this._socket as any)._namespaceKey = socketKey;

      this._socket.on("connect", () => {
        this._logger.debug("WebSocket connected");
        this.isConnected.set(true);
      });

      this._socket.on("disconnect", (reason) => {
        this._logger.warn(`WebSocket disconnected: ${reason}`);
        this.isConnected.set(false);
        this.handleUnexpectedDisconnection(reason);
      });

      this._socket.on("reconnect", (attemptNumber) => {
        this._logger.debug(`WebSocket reconnected after ${attemptNumber} attempts`);
        this.isConnected.set(true);
      });

      this._socket.on("reconnect_attempt", (attemptNumber) => {
        this._logger.debug(`WebSocket reconnection attempt ${attemptNumber}`);
        // Update auth token on each reconnection attempt
        if (this._socket) {
          this._socket.auth = { token: this._auth.accessToken };
        }
      });

      this._socket.on("reconnect_error", (err) => {
        this._logger.warn("WebSocket reconnect_error", err?.message || err);
      });

      this._socket.on("reconnect_failed", () => {
        this._logger.error("WebSocket reconnection failed after all attempts");
        this._toast.error("Lost connection to server. Please refresh the page.");
      });

      this._socket.on("pong", () => {
        // Pong received, connection is alive
        this._logger.debug("Heartbeat: Pong received, connection is alive");
      });

      this._socket.on("connect_error", (err) => {
        this._logger.warn("WS connect_error", err?.message || err);
        // Force socket to use fresh token on reconnection
        if (this._socket) {
          this._socket.auth = { token: this._auth.accessToken };
        }
      });

      // Add online listener to handle recovery from sleep
      if (!this._onlineListener) {
        this._onlineListener = (): void => {
          this._logger.debug("Browser is back online, ensuring socket connection.");
          if (this._socket && !this._socket.connected) {
            this._socket.connect();
          }
        };
        window.addEventListener("online", this._onlineListener);
      }

      // Register stream routing handlers for this socket instance
      this.registerStreamHandlers(socketKey);
    }
    return this._socket!;
  }

  constructor() {
    super();
    // Watch for user changes and reconnect socket if needed
    effect(() => {
      const currentUser = this._auth.currentUser();
      const newUserId = currentUser?.id || null;

      // If user changed (including logout), reconnect socket
      if (this._currentUserId !== null && this._currentUserId !== newUserId) {
        this._logger.debug(
          `User changed from ${this._currentUserId} to ${newUserId}, reconnecting socket`
        );
        this.reconnectForUser();
      }

      this._currentUserId = newUserId;
    });
  }

  /**
   * Handle unexpected disconnections by aborting active streams
   */
  private handleUnexpectedDisconnection(reason: string): void {
    const errorPayload = {
      code: "DISCONNECTED",
      message: "Connection to server lost. Please check your internet connection.",
      details: reason
    };

    // Abort all in-flight streams (supports multiple concurrent streams)
    if (this._assistRequests.size > 0) {
      this._logger.warn("Aborting assistant streams due to disconnection");
      for (const subj of this._assistRequests.values()) {
        try {
          if (!subj.closed) subj.error(errorPayload);
        } catch {}
      }
      this._assistRequests.clear();
    }

    if (this._chatRequests.size > 0) {
      this._logger.warn("Aborting chat streams due to disconnection");
      for (const subj of this._chatRequests.values()) {
        try {
          if (!subj.closed) subj.error(errorPayload);
        } catch {}
      }
      this._chatRequests.clear();
    }

    this._activeStreamCount = 0;
    this.stopHeartbeat();
  }

  /**
   * Join a document room for document-specific messages
   */
  private joinDocumentRoom(documentId: string): void {
    if (this._socket?.connected) {
      this._socket.emit("join-document", { documentId });
      this._logger.debug(`Joined document room: ${documentId}`);
    }
  }

  /**
   * Ensure socket is connected before emitting with improved sleep/wake handling
   */
  private ensureConnected(): Observable<void> {
    if (this.socket.connected) {
      return of(void 0);
    }

    this._logger.debug("Socket not connected, waiting for connection...");

    return new Observable<void>((observer) => {
      // If already connected by the time we get here
      if (this.socket.connected) {
        observer.next();
        observer.complete();
        return;
      }

      let timeout: NodeJS.Timeout;
      let reconnectAttempts = 0;
      const maxReconnectAttempts = 3;
      const baseTimeoutMs = 15000; // 15 seconds for sleep/wake scenarios

      const attemptConnection = (): void => {
        reconnectAttempts++;

        // Clear any existing timeout
        if (timeout) {
          clearTimeout(timeout);
        }

        // Set timeout based on attempt number (longer for subsequent attempts)
        const currentTimeoutMs = baseTimeoutMs + (reconnectAttempts - 1) * 5000;

        timeout = setTimeout(() => {
          this.socket.off("connect", onConnect);
          this.socket.off("reconnect", onReconnect);

          if (reconnectAttempts < maxReconnectAttempts) {
            this._logger.debug(`Connection timeout on attempt ${reconnectAttempts}, trying again...`);
            attemptConnection();
          } else {
            observer.error(new Error("Connection timeout after multiple attempts - please check your network connection"));
          }
        }, currentTimeoutMs);

        const onConnect = (): void => {
          clearTimeout(timeout);
          this.socket.off("connect", onConnect);
          this.socket.off("reconnect", onReconnect);
          this._logger.debug("Socket connected, proceeding with request");
          observer.next();
          observer.complete();
        };

        const onReconnect = (): void => {
          clearTimeout(timeout);
          this.socket.off("connect", onConnect);
          this.socket.off("reconnect", onReconnect);
          this._logger.debug("Socket reconnected, proceeding with request");
          observer.next();
          observer.complete();
        };

        this.socket.once("connect", onConnect);
        this.socket.once("reconnect", onReconnect);

        // Force reconnection if not already connecting
        if (!this.socket.active) {
          this._logger.debug(`Forcing socket connection (attempt ${reconnectAttempts})`);
          this.socket.connect();
        }
      };

      // Start first connection attempt
      attemptConnection();
    });
  }

  private createRequestId(): string {
    try {
      return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    } catch {
      return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
  }

  private beginStream(): void {
    if (this._activeStreamCount === 0) {
      this.startHeartbeat();
    }
    this._activeStreamCount++;
  }

  private endStream(): void {
    this._activeStreamCount = Math.max(0, this._activeStreamCount - 1);
    if (this._activeStreamCount === 0) {
      this.stopHeartbeat();
    }
  }

  private getSubjectByRequestId<T>(map: Map<string, Subject<T>>, requestId?: string): Subject<T> | null {
    if (requestId && map.has(requestId)) {
      return map.get(requestId)!;
    }
    // Backward-compatible fallback (older servers might not send requestId)
    if (!requestId && map.size === 1) {
      return Array.from(map.values())[0]!;
    }
    return null;
  }

  private registerStreamHandlers(socketKey: string): void {
    if (!this._socket) return;
    if (this._handlersRegisteredForSocketKey === socketKey) return;
    this._handlersRegisteredForSocketKey = socketKey;

    const onAssistStarted = (data: { actionId: string; requestId?: string }): void => {
      const subject = this.getSubjectByRequestId(this._assistRequests, data?.requestId);
      if (!subject) return;
      this._logger.debug("[AI][WS] assist-started", {
        requestId: data?.requestId,
        actionId: data?.actionId
      });
      subject.next({ status: "started", actionId: data.actionId });
    };

    const onAssistThought = (data: { thought: string; actionId: string; requestId?: string }): void => {
      const subject = this.getSubjectByRequestId(this._assistRequests, data?.requestId);
      if (!subject) return;
      subject.next({ status: "thought", thought: data.thought, actionId: data.actionId });
    };

    const onAssistEnd = (data: AiAssistStreamEnd & { requestId?: string }): void => {
      const subject = this.getSubjectByRequestId(this._assistRequests, (data as any)?.requestId);
      if (!subject) return;
      this._logger.debug("[AI][WS] assist-end", {
        requestId: (data as any)?.requestId,
        actionId: (data as any)?.actionId,
        opsCount: Array.isArray((data as any)?.finalOutput) ? (data as any).finalOutput.length : undefined
      });
      subject.next({
        status: "completed",
        finalOutput: data.finalOutput,
        actionId: data.actionId,
        suggestions: (data as any).suggestions,
        information: data.information
      });
      subject.complete();
      if ((data as any)?.requestId) {
        this._assistRequests.delete((data as any).requestId);
      } else if (this._assistRequests.size === 1) {
        this._assistRequests.clear();
      }
      this.endStream();
    };

    const onChatStarted = (data: { assistantMessageId: string; requestId?: string }): void => {
      const subject = this.getSubjectByRequestId(this._chatRequests, data?.requestId);
      if (!subject) return;
      this._logger.debug("[AI][WS] chat-started", {
        requestId: data?.requestId,
        assistantMessageId: data?.assistantMessageId
      });
      subject.next({ status: "started", assistantMessageId: data.assistantMessageId });
    };

    const onChatStream = (data: AiChatStreamChunk & { requestId?: string }): void => {
      const subject = this.getSubjectByRequestId(this._chatRequests, (data as any)?.requestId);
      if (!subject) return;
      subject.next({ status: "chunk", chunk: (data as any).chunk });
    };

    const onChatEnd = (data: AiChatStreamEnd & { requestId?: string }): void => {
      const subject = this.getSubjectByRequestId(this._chatRequests, (data as any)?.requestId);
      if (!subject) return;
      this._logger.debug("[AI][WS] chat-end", {
        requestId: (data as any)?.requestId,
        assistantMessageId: (data as any)?.assistantMessageId || (data as any)?.response_id,
        finalLen: ((data as any)?.response_content || "").length
      });
      subject.next({
        status: "completed",
        assistantMessageId: (data as any).assistantMessageId || (data as any).response_id,
        finalContent: (data as any).response_content
      });
      subject.complete();
      if ((data as any)?.requestId) {
        this._chatRequests.delete((data as any).requestId);
      } else if (this._chatRequests.size === 1) {
        this._chatRequests.clear();
      }
      this.endStream();
    };

    const onChatError = (err: any): void => {
      const requestId: string | undefined = err?.requestId;
      const normalized =
        typeof err === "string"
          ? { code: err, message: err }
          : {
              code: err?.code || err?.error?.code,
              message: err?.message || err?.error?.message || "",
              details: err?.details
            };

      if (requestId && this._chatRequests.has(requestId)) {
        const subject = this._chatRequests.get(requestId)!;
        try {
          subject.error(normalized);
        } catch {}
        this._chatRequests.delete(requestId);
        this.endStream();
        return;
      }

      // If we can't attribute the error, fail all active chat streams.
      for (const subj of this._chatRequests.values()) {
        try {
          subj.error(normalized);
        } catch {}
        this.endStream();
      }
      this._chatRequests.clear();
    };

    // Register once for this socket instance
    this._socket.on("document-assist-started", onAssistStarted);
    this._socket.on("document-assist-thought", onAssistThought);
    this._socket.on("document-assist-end", onAssistEnd);
    this._socket.on("document-chat-started", onChatStarted);
    this._socket.on("document-chat-stream", onChatStream);
    this._socket.on("document-chat-end", onChatEnd);
    this._socket.on("chat-error", onChatError);
  }

  assist(prompt: string, modelId: string, documentId: string, context?: DocumentContext): Observable<AiAssistEvent> {
    const hasFiles = this.contextHasFiles(context);

    if (hasFiles) {
      return this.uploadFilesAndAssistWithSocket(prompt, modelId, documentId, context!);
    }

    return this.ensureConnected().pipe(
      switchMap(() => {
        this.joinDocumentRoom(documentId);

        const requestId = this.createRequestId();
        const subject = new Subject<AiAssistEvent>();
        this._assistRequests.set(requestId, subject);
        this.beginStream();

        const payload = { prompt, documentId, modelId, context, requestId };

        try {
          this.socket.emit("document-assist", payload, (ack: { error?: string }) => {
            if (!ack?.error) return;
            const normalized = { code: ack.error, message: ack.error };
            this._logger.error("Assist emit acknowledgment error", normalized);
            try {
              subject.error(normalized);
            } catch {}
            this._assistRequests.delete(requestId);
            this.endStream();
          });
        } catch (e) {
          this._logger.error("Assist emit failed", e);
          this._toast.error("Connection error while starting assistant");
          try {
            subject.error({ code: "EMIT_FAILED", message: (e as any)?.message || "Failed to send request" });
          } catch {}
          this._assistRequests.delete(requestId);
          this.endStream();
        }

        return subject.asObservable();
      })
    );
  }

  chat(prompt: string, modelId: string, documentId: string, context?: DocumentContext): Observable<AiChatEvent> {
    const hasFiles = this.contextHasFiles(context);

    if (hasFiles) {
      return this.uploadFilesAndChatWithSocket(prompt, modelId, documentId, { ...context });
    }

    return this.ensureConnected().pipe(
      switchMap(() => {
        this.joinDocumentRoom(documentId);

        const requestId = this.createRequestId();
        const subject = new Subject<AiChatEvent>();
        this._chatRequests.set(requestId, subject);
        this.beginStream();

        const payload = { content: prompt, documentId, modelId, context: { ...context }, requestId };
        try {
          this.socket.emit("document-chat", payload, (ack: { error?: string }) => {
            if (ack?.error) {
              const normalized = { code: ack.error, message: ack.error };
              this._logger.error("Chat emit acknowledgment error", normalized);
              try {
                subject.error(normalized);
              } catch {}
              this._chatRequests.delete(requestId);
              this.endStream();
            }
          });
        } catch (e) {
          this._logger.error("Chat emit failed", e);
          try {
            subject.error({ code: "EMIT_FAILED", message: (e as any)?.message || "" });
          } catch {}
          this._chatRequests.delete(requestId);
          this.endStream();
        }

        return subject.asObservable();
      })
    );
  }

  stopChat(assistantMessageId: string, partialContent: string): void {
    try {
      this.socket.emit("stop-document-chat", { assistantMessageId, partialContent });
    } catch (e) {
      this._logger.error("Stop chat emit failed", e);
      this._toast.error("Connection error while stopping chat");
    }
  }

  /**
   * Start heartbeat monitoring to detect connection drops
   */
  private startHeartbeat(): void {
    this.stopHeartbeat(); // Clear any existing heartbeat

    this._heartbeatInterval = setInterval(() => {
      if (this._socket && !this._socket.connected) {
        this._logger.warn("Heartbeat: Socket not connected, stopping heartbeat");
        this.stopHeartbeat();
        return;
      }

      // Send a ping to check if connection is alive
      if (this._socket) {
        this._socket.emit("ping");
      }
    }, 30000); // Check every 30 seconds
  }

  /**
   * Stop heartbeat monitoring
   */
  private stopHeartbeat(): void {
    if (this._heartbeatInterval) {
      clearInterval(this._heartbeatInterval);
      this._heartbeatInterval = null;
    }
  }

  /**
   * Disconnect and cleanup socket connection (call on logout)
   */
  disconnect(): void {
    if (this._socket) {
      this._logger.debug("Disconnecting WebSocket");
      // Error out active streams on manual disconnect
      const errorPayload = { code: "DISCONNECTED", message: "Client disconnected" };
      for (const subj of this._assistRequests.values()) {
        try {
          if (!subj.closed) subj.error(errorPayload);
        } catch {}
      }
      for (const subj of this._chatRequests.values()) {
        try {
          if (!subj.closed) subj.error(errorPayload);
        } catch {}
      }
      this._assistRequests.clear();
      this._chatRequests.clear();
      this._activeStreamCount = 0;
      this.stopHeartbeat();
      this._socket.disconnect();
      this._socket = undefined;
      this._handlersRegisteredForSocketKey = null;
      this.isConnected.set(false);
    }
    if (this._onlineListener) {
      window.removeEventListener("online", this._onlineListener);
      this._onlineListener = undefined;
    }
  }

  /**
   * Reconnect socket when user changes (e.g., after login)
   */
  reconnectForUser(): void {
    if (this._socket) {
      this._logger.debug("Reconnecting WebSocket for new user");
      this.disconnect();
    }
    // Socket will be recreated on next access with new user's namespace
  }

  /**
   * Centralized method to upload files and then initiate assist via WebSocket
   */
  private uploadFilesAndAssistWithSocket(
    prompt: string,
    modelId: string,
    documentId: string,
    context: DocumentContext
  ): Observable<AiAssistEvent> {
    return this.uploadFiles(context).pipe(
      switchMap((attachmentIds) => {
        const newContext = { ...context, attachments: attachmentIds as unknown as AttachmentContext[] };
        // Re-use the existing WebSocket logic by calling assist again, but now without files
        return this.assist(prompt, modelId, documentId, newContext);
      })
    );
  }

  /**
   * Centralized method to upload files and then initiate chat via WebSocket
   */
  private uploadFilesAndChatWithSocket(
    prompt: string,
    modelId: string,
    documentId: string,
    context: DocumentContext
  ): Observable<AiChatEvent> {
    return this.uploadFiles(context).pipe(
      switchMap((attachmentIds) => {
        this._logger.debug("Received attachment IDs from upload:", attachmentIds);
        const newContext = { ...context, attachments: attachmentIds as unknown as AttachmentContext[] };
        this._logger.debug("Context being passed to chat method:", newContext);
        // Re-use the existing WebSocket logic by calling chat again, but now without files
        return this.chat(prompt, modelId, documentId, newContext);
      })
    );
  }

  /**
   * Uploads files from context and returns an observable with attachment ID objects
   */
  private uploadFiles(context: DocumentContext): Observable<{ id: string; filename: string }[]> {
    const files = this.extractFilesFromContext(context);
    if (files.length === 0) {
      return from([[]]); // No files to upload
    }

    this._logger.debug(`Uploading ${files.length} files...`);

    return this._fileUploadService.uploadFiles(files).pipe(
      map((response) => response.attachmentIds),
      tap((ids) => this._logger.debug(`Files uploaded successfully, received IDs:`, ids)),
      catchError((error) => {
        this._logger.error("File upload failed:", error);
        this._toast.error("Failed to upload attachments.");
        throw error;
      })
    );
  }

  /**
   * Checks if the document context contains any file attachments.
   */
  private contextHasFiles(context?: DocumentContext): boolean {
    return (
      !!context?.attachments &&
      Array.isArray(context.attachments) &&
      context.attachments.some((att: any) => att.file instanceof File)
    );
  }

  /**
   * Extract File objects from context attachments
   */
  private extractFilesFromContext(context?: DocumentContext): File[] {
    if (!context?.attachments || !Array.isArray(context.attachments)) {
      return [];
    }

    return context.attachments.filter((att) => att.file instanceof File).map((att) => att.file);
  }
}
